// SAB-577 / SAB-593: the persisted NFL active-league selection.
//
// The bug: NFLLeagueContext picked leagueData[0] from an unordered query with no
// persistence, so a commissioner in several leagues got an arbitrary one on reload
// and the page rendered another league's invite code and roster size. This module
// holds the pure resolution (unit-tested) and the localStorage persistence, mirroring
// MLB's LeagueContext 'sf_active_league_id'. A separate key so the sports don't clash.

export const NFL_ACTIVE_LEAGUE_KEY = 'sf_active_nfl_league_id';

/** Persist the active NFL league id across a reload. Used by the create and join
 *  flows (which reload) so a freshly created/joined league becomes active rather
 *  than reverting to the deterministic default, and by the in-app switcher. */
export function persistNflActiveLeagueId(id: string | null | undefined): void {
  try {
    if (id) localStorage.setItem(NFL_ACTIVE_LEAGUE_KEY, id);
  } catch {
    /* localStorage unavailable (private mode / SSR) — non-fatal */
  }
}

export function readNflActiveLeagueId(): string | null {
  try {
    return localStorage.getItem(NFL_ACTIVE_LEAGUE_KEY);
  } catch {
    return null;
  }
}

export function clearNflActiveLeagueId(): void {
  try {
    localStorage.removeItem(NFL_ACTIVE_LEAGUE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Resolve which NFL league should be active. Pure and deterministic.
 *
 *   - honor the persisted (cached) selection ONLY if it is still a league the user
 *     is currently a member of (covers leagues left/deleted and account switches);
 *   - otherwise fall back to the FIRST of the deterministically-ordered leagues
 *     (the caller orders by created_at asc). Never returns an arbitrary id.
 *
 * @param cachedId          persisted selection, or null
 * @param memberLeagueIds   leagues the user is currently a member of
 * @param orderedLeagueIds  the user's league ids in a deterministic order
 * @returns the id to make active, or null if the user has no leagues
 */
export function resolveActiveNflLeagueId(
  cachedId: string | null,
  memberLeagueIds: readonly string[],
  orderedLeagueIds: readonly string[],
): string | null {
  if (orderedLeagueIds.length === 0) return null;
  if (cachedId && memberLeagueIds.includes(cachedId)) return cachedId;
  return orderedLeagueIds[0];
}
